import React, { useEffect } from "react";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const PAGES_THRESHOLD = 5;

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
    const handlePrev = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    useEffect(() => {
        // If the total number of pages shrinks (for example after applying filters),
        if (totalPages <= 0) {
            if (currentPage !== 1) {
                onPageChange(1);
            }
            return;
        }

        if (currentPage > totalPages) {
            onPageChange(Math.max(1, totalPages));
        }
    }, [currentPage, totalPages, onPageChange]);

    // Generate smart page number array with ellipsis
    const getPageNumbers = () => {
        const pages: (number | { type: 'ellipsis'; id: string })[] = [];

        // If small number of pages, show all
        if (totalPages <= PAGES_THRESHOLD) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        // Always show first page
        pages.push(1);

        // Build a centered window of up to 2 pages around currentPage
        const windowSize = 2;
        let start = Math.max(2, currentPage - 1);
        let end = Math.min(totalPages - 1, currentPage + 1);

        // Expand window if near edges to guarantee windowSize where possible
        while (end - start + 1 < windowSize) {
            if (start > 2) {
                start -= 1;
            } else if (end < totalPages - 1) {
                end += 1;
            } else {
                break;
            }
        }

        if (start > 2) {
            pages.push({ type: 'ellipsis', id: `ellipsis-before-${start}` });
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < totalPages - 1) {
            pages.push({ type: 'ellipsis', id: `ellipsis-after-${end}` });
        }

        // Always show last page
        pages.push(totalPages);

        return pages;
    };

    const pageNumbers = getPageNumbers();

    const getPrevClassNames = () => {
        let classes = 'pagination__item pagination__item--prev';
        if (currentPage <= 1) {
            classes += ' is-disabled';
        }
        return classes;
    };

    const getNextClassNames = () => {
        let classes = 'pagination__item pagination__item--next';
        if (currentPage >= totalPages) {
            classes += ' is-disabled';
        }
        return classes;
    };

    return (
        <nav className="pagination" aria-label="Pagination navigation">
            <ul className="pagination__list">
                <li
                    className={getPrevClassNames()}
                >
                    <button className="pagination__link se-icon--previous" aria-label="Previous page" disabled={currentPage <= 1} onClick={handlePrev}>
                    </button>
                </li>

                {pageNumbers.map(page => {
                    if (typeof page === 'object' && page.type === 'ellipsis') {
                        return (
                            <li key={page.id} className="pagination__item pagination__item--ellipsis">
                                <span className="pagination__ellipsis" aria-hidden="true" tabIndex={-1}>...</span>
                            </li>
                        );
                    }

                    const pageNum = page as number;
                    const isActive = currentPage === pageNum;

                    let itemClassName = 'pagination__item';
                    if (isActive) {
                        itemClassName += ' pagination__item--active';
                    }

                    let linkClassName = 'pagination__link';
                    if (isActive) {
                        linkClassName += ' is-active';
                    }

                    const buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
                        className: linkClassName,
                        'aria-label': `Go to page ${pageNum}`,
                        onClick: () => onPageChange(pageNum),
                    };

                    if (isActive) {
                        buttonProps['aria-current'] = 'page';
                    }

                    return (
                        <li
                            key={pageNum}
                            className={itemClassName}
                        >
                            <button {...buttonProps}>
                                {pageNum}
                            </button>
                        </li>
                    );
                })}

                <li
                    className={getNextClassNames()}
                >
                    <button className="pagination__link se-icon--next" aria-label="Next page" disabled={currentPage >= totalPages} onClick={handleNext}>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;
